import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";

const raleway = { fontFamily: "'Geist', sans-serif" };
const geistMono = { fontFamily: "'Geist Mono', monospace" };
const instrumentSerif = { fontFamily: "'Instrument Serif', Georgia, serif" };

const FAQS = [
  {
    q: "How do I open a Zikora MFB account?",
    a: "Download the Zikora MFB App, enter your details, complete your BVN verification, and start banking in just a few minutes.",
  },
  {
    q: "Is Zikora MFB licensed?",
    a: "Yes. Zikora Microfinance Bank is licensed and regulated by the Central Bank of Nigeria (CBN) and insured by the Nigeria Deposit Insurance Corporation (NDIC).",
  },
  {
    q: "How can I apply for a loan?",
    a: "You can apply for a loan directly through the Zikora MFB App or by visiting any of our branches. Eligibility is based on your account activity and credit history.",
  },
  {
    q: "Can I transfer money using the app?",
    a: "Yes. You can transfer money to any Nigerian bank account instantly, 24/7, directly from the Zikora MFB mobile app.",
  },
  {
    q: "Where is Zikora MFB located?",
    a: "Zikora Microfinance Bank is headquartered in Onitsha, Anambra State, with branches and agency banking points across South-East Nigeria.",
  },
];

const ChevronUp = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 10L8 6L4 10"
      stroke="#132A22"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronDown = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 6L8 10L12 6"
      stroke="#6B7B70"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FAQItem = ({ q, a, open, onToggle }) => (
  <div style={{ borderBottom: "1px solid #E5E5E5" }}>
    <button
      onClick={onToggle}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "16px",
        padding: "clamp(16px, 1.67vw, 24px) 0",
        background: "none",
        border: "none",
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <span
        style={{
          ...raleway,
          fontWeight: 600,
          fontSize: "clamp(15px, 1.25vw, 18px)",
          lineHeight: "1.4",
          color: "#132A22",
        }}
      >
        {q}
      </span>
      <div
        style={{
          width: "32px",
          height: "32px",
          borderRadius: "50%",
          backgroundColor: "#F5F5F5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {open ? <ChevronUp /> : <ChevronDown />}
      </div>
    </button>

    {open && (
      <p
        style={{
          ...raleway,
          fontWeight: 400,
          fontSize: "clamp(13px, 1.11vw, 16px)",
          lineHeight: "1.75",
          color: "#3F5445",
          margin: "0 0 clamp(16px, 1.67vw, 24px) 0",
          maxWidth: "90%",
        }}
      >
        {a}
      </p>
    )}
  </div>
);

const LoanFAQ = () => {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <>
      <style>{`
				@media (max-width: 767px) {
					.lfaq-inner {
						flex-direction: column !important;
						gap: 24px !important;
					}
					.lfaq-left { display: contents !important; }
					.lfaq-heading-group { order: 1 !important; }
					.lfaq-right {
						order: 2 !important;
						flex: 0 0 auto !important;
						width: 100% !important;
					}
					.lfaq-cta { order: 3 !important; }
				}
			`}</style>

      <section
        style={{
          width: "100%",
          backgroundColor: "#FFFFFF",
          boxSizing: "border-box",
          padding: "clamp(48px, 6.94vw, 100px) clamp(20px, 5.56vw, 80px)",
        }}
      >
        <div
          className="lfaq-inner"
          style={{
            maxWidth: "1280px",
            margin: "0 auto",
            display: "flex",
            alignItems: "flex-start",
            gap: "clamp(32px, 8.33vw, 120px)",
            flexWrap: "wrap",
          }}
        >
          {/* Left — heading + CTA */}
          <div
            className="lfaq-left"
            style={{
              flex: "1 1 280px",
              maxWidth: "480px",
              display: "flex",
              flexDirection: "column",
              gap: "clamp(24px, 2.78vw, 40px)",
            }}
          >
            <div
              className="lfaq-heading-group"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "clamp(12px, 1.39vw, 20px)",
              }}
            >
              {/* Label */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#295B4A",
                    flexShrink: 0,
                    display: "inline-block",
                  }}
                />
                <span
                  style={{
                    ...geistMono,
                    fontWeight: 500,
                    fontSize: "12px",
                    lineHeight: "1",
                    letterSpacing: "0.1em",
                    color: "#295B4A",
                    textTransform: "uppercase",
                  }}
                >
                  FAQs
                </span>
              </div>

              <h2
                style={{
                  ...instrumentSerif,
                  fontWeight: 400,
                  fontSize: "clamp(28px, 4.17vw, 60px)",
                  lineHeight: 1.1,
                  letterSpacing: "-0.01em",
                  color: "#132A22",
                  margin: 0,
                }}
              >
                Frequently asked questions you need to know before applying for
                a loan.
              </h2>
            </div>

            <RouterLink
              to="/contact"
              className="lfaq-cta"
              style={{
                ...raleway,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                height: "clamp(44px, 3.61vw, 52px)",
                padding: "0 clamp(16px, 1.67vw, 24px)",
                border: "1px solid #132A22",
                borderRadius: "8px",
                fontWeight: 500,
                fontSize: "clamp(13px, 1.11vw, 16px)",
                color: "#132A22",
                textDecoration: "none",
                whiteSpace: "nowrap",
                alignSelf: "flex-start",
              }}
            >
              Have more questions? Contact Us
            </RouterLink>
          </div>

          {/* Right — accordion */}
          <div className="lfaq-right" style={{ flex: "2 1 320px", minWidth: 0 }}>
            {FAQS.map((item, idx) => (
              <FAQItem
                key={idx}
                q={item.q}
                a={item.a}
                open={openIdx === idx}
                onToggle={() => setOpenIdx(openIdx === idx ? null : idx)}
              />
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default LoanFAQ;
